// packages/i18n/src/createNamespacedTranslator.ts
//
// The ONE place a translator is built from an already-loaded dictionary.
//
// Browser-safe: no filesystem, no `server-only`, no next-intl request scope. The Node
// workers, the API composers, the React islands on the marketing site and the backoffice
// server components all build translators this way, and they used to do it by hand:
//
//     const t = createTranslator({ locale, messages });
//
// That was 49 copies of the same three lines, and it is now also WRONG, because messages
// are nested under their namespace (see `server/loadMessages.ts`). Passing a flat map with
// no namespace gives a translator whose key space is the whole map with no `AppConfig`
// entry behind it, which is exactly the untyped hole this migration closes.
//
// No `getMessageFallback` is passed, so a miss gets use-intl's default: the NAMESPACED key,
// e.g. `common.Save` — the same thing server `getTranslations('common')` returns. Only the
// client `I18nProvider` (`client/contexts/I18nContext.tsx`) overrides it to return the bare
// key. So never detect a miss by comparing `t(key)` to `key`: it can never match here or on
// the server. Probe with `t.has` instead — `resolveCommonKey`, `translateOpenKeyOrNull`,
// `translateUnknownKeyOrNull` (`openKeys.ts`) do that for you.

import { createTranslator } from 'use-intl/core';
import type { AppLocale } from '@tourtask/types/i18n';
import type { CommonT, PublicT } from './keys';

interface FromMessagesProps {
  locale: AppLocale;
  /** A FLAT dictionary — `{ Save: 'Guardar' }`, as the generated JSON and the strings API deliver it. */
  messages: Record<string, string>;
}

/**
 * A `common`-namespace translator over an already-loaded flat dictionary.
 *
 * The dictionary may be a SUBSET (a picked island subset, embed's fetch list). The return
 * type says "these keys are real", not "these keys were loaded" — `has` answers the second
 * question at runtime, which is why `CommonT` carries it.
 */
export function createCommonTranslatorFromMessages({ locale, messages }: FromMessagesProps): CommonT {
  return createTranslator({
    locale,
    messages: { common: messages },
    namespace: 'common',
  });
}

/** {@link createCommonTranslatorFromMessages} for the marketing site's `public` namespace. */
export function createPublicTranslatorFromMessages({ locale, messages }: FromMessagesProps): PublicT {
  return createTranslator({
    locale,
    messages: { public: messages },
    namespace: 'public',
  });
}
